<script>
  import { onMount } from 'svelte';
  import { currentScreen, showScreen } from './stores/screen.js';
  import { session, connectionStatus, initSession, checkPairing, initLinkSession, updateConnectionStatus } from './stores/session.js';
  import { scannedPages, clearPages } from './stores/pages.js';
  import { scanMode, initSettings } from './stores/settings.js';
  import { trackScreenChange } from './sentry.js';
  import { trackEvent } from './utils/analytics.js';
  import { attemptReconnectViaPairing, attemptReconnectPolling, startSessionHealthCheck } from './lib/session-health.js';
  import { setupTabChannelListener, checkExistingTab, sendTakeSession, setOnTakeSession } from './utils/tab-manager.js';
  import { t } from './i18n/index.js';

  // Screen components
  import PairingScreen from './screens/PairingScreen.svelte';
  import CameraScreen from './screens/CameraScreen.svelte';
  import ThumbnailsScreen from './screens/ThumbnailsScreen.svelte';
  import UploadingScreen from './screens/UploadingScreen.svelte';
  import CompleteScreen from './screens/CompleteScreen.svelte';
  import ErrorScreen from './screens/ErrorScreen.svelte';
  import SettingsScreen from './screens/SettingsScreen.svelte';
  import LandingScreen from './screens/LandingScreen.svelte';
  import LinkConfirmScreen from './screens/LinkConfirmScreen.svelte';

  // Common components
  import Toast from './components/common/Toast.svelte';
  import ConnectionStatus from './components/common/ConnectionStatus.svelte';

  // Dialogs
  import DisconnectedDialog from './components/common/DisconnectedDialog.svelte';
  import RestoreDialog from './components/common/RestoreDialog.svelte';
  import ModeSwitchDialog from './components/common/ModeSwitchDialog.svelte';

  // Dialog state
  let showDisconnectedDialog = $state(false);
  let showRestoreDialog = $state(false);
  let showModeSwitchDialog = $state(false);
  let pendingModeSwitch = $state(null);
  let pendingRestoreImage = $state(null);

  // Tab redirect state (new tab was redirected to existing tab)
  let redirectedToOtherTab = $state(false);

  // Track screen changes for Sentry + analytics
  $effect(() => {
    if ($currentScreen) {
      trackScreenChange($currentScreen);
      trackEvent('screen_viewed', { screen: $currentScreen });
    }
  });

  onMount(async () => {
    // Initialize settings
    initSettings();

    // Wire up tab channel listener so this tab responds to BroadcastChannel messages
    setupTabChannelListener();

    // Register take_session callback: when another tab sends a QR code, handle it here
    setOnTakeSession(async (code) => {
      try {
        console.log('Taking session from another tab, code:', code);
        await initSession({ shortCode: code });
        const isPaired = checkPairing();
        if (isPaired) {
          showScreen('camera');
          return true;
        }
        return false;
      } catch (e) {
        console.error('Failed to take session from other tab:', e);
        return false;
      }
    });

    // Check link entry first (/l/{code})
    const isLinkEntry = window.location.pathname.startsWith('/l/');
    if (isLinkEntry) {
      try {
        const linkResult = await initLinkSession();
        if (linkResult) {
          showScreen('linkConfirm');
          return;
        }
        // Invalid link format, fall through to normal flow
      } catch (e) {
        console.error('Link init failed:', e);
        // Fall through to normal flow
      }
    }

    // If this is a /s/{code} URL, try redirecting to existing tab first
    const shortCodeMatch = window.location.pathname.match(/^\/s\/([A-HJ-NP-Z2-9]{7})$/i);
    if (shortCodeMatch) {
      const hasExistingTab = !(await checkExistingTab());
      if (hasExistingTab) {
        // Existing tab found — send QR code to it and show redirect screen
        const code = shortCodeMatch[1].toUpperCase();
        const taken = await sendTakeSession(code);
        if (taken) {
          // Clean URL and show redirect screen
          window.history.replaceState({}, document.title, '/');
          redirectedToOtherTab = true;
          window.close(); // attempt to close; may be blocked by browser
          return;
        }
        // If existing tab failed to take session, fall through to normal flow
      }
    }

    // Initialize session from localStorage or URL
    await initSession();

    // Determine initial screen
    const isPaired = checkPairing();

    // Check if this is a QR entry point
    const isQrEntry = window.location.hostname.startsWith('go.') ||
                      window.location.pathname.startsWith('/s/') ||
                      window.location.pathname.startsWith('/pair');

    if (isPaired) {
      showScreen('camera');
    } else if (isQrEntry) {
      showScreen('pairing');
    } else {
      showScreen('landing');
    }
  });

  // Handle session disconnection - show "reconnecting" status and poll for ~30s before dialog
  async function handleDisconnected(reason) {
    if (reason === 'session_not_found' || reason === 'session_expired') {
      // Show reconnecting status on camera screen instead of dialog
      updateConnectionStatus('reconnecting');

      const reconnected = await attemptReconnectPolling();
      if (reconnected) {
        startSessionHealthCheck(
          (r) => handleDisconnected(r),
          (isOnline, name) => {
            updateConnectionStatus(isOnline ? 'connected' : 'disconnected', name);
          }
        );
        return;
      }
    }
    showDisconnectedDialog = true;
  }

  // Handle mode switch with confirmation
  function handleModeSwitch(newMode) {
    if ($scannedPages.length > 0 && $scanMode === 'document') {
      pendingModeSwitch = newMode;
      showModeSwitchDialog = true;
    } else {
      scanMode.set(newMode);
    }
  }

  function confirmModeSwitch() {
    if (pendingModeSwitch) {
      clearPages();
      scanMode.set(pendingModeSwitch);
      pendingModeSwitch = null;
    }
    showModeSwitchDialog = false;
  }

  function cancelModeSwitch() {
    pendingModeSwitch = null;
    showModeSwitchDialog = false;
  }
</script>

<div class="app h-full flex flex-col">
  {#if redirectedToOtherTab}
    <div class="redirect-screen">
      <div class="redirect-content">
        <div class="redirect-icon">✓</div>
        <h2>{t('redirect.title')}</h2>
        <p>{t('redirect.message')}</p>
        <p class="redirect-hint">{t('redirect.closeHint')}</p>
      </div>
    </div>
  {:else if $currentScreen === 'landing'}
    <LandingScreen />
  {:else if $currentScreen === 'pairing'}
    <PairingScreen />
  {:else if $currentScreen === 'linkConfirm'}
    <LinkConfirmScreen />
  {:else if $currentScreen === 'camera'}
    <CameraScreen
      onModeSwitch={handleModeSwitch}
      onDisconnected={handleDisconnected}
    />
  {:else if $currentScreen === 'thumbnails'}
    <ThumbnailsScreen />
  {:else if $currentScreen === 'uploading'}
    <UploadingScreen />
  {:else if $currentScreen === 'complete'}
    <CompleteScreen />
  {:else if $currentScreen === 'settings'}
    <SettingsScreen />
  {:else if $currentScreen === 'error'}
    <ErrorScreen />
  {/if}

  <!-- Global components -->
  <Toast />

  <!-- Dialogs -->
  {#if showDisconnectedDialog}
    <DisconnectedDialog
      onClose={() => showDisconnectedDialog = false}
      onReconnect={async () => {
        showDisconnectedDialog = false;
        updateConnectionStatus('reconnecting');

        const reconnected = await attemptReconnectPolling();
        if (reconnected) {
          startSessionHealthCheck(
            (r) => handleDisconnected(r),
            (isOnline, name) => {
              updateConnectionStatus(isOnline ? 'connected' : 'disconnected', name);
            }
          );
        } else {
          showScreen('pairing');
        }
      }}
    />
  {/if}

  {#if showRestoreDialog && pendingRestoreImage}
    <RestoreDialog
      imageData={pendingRestoreImage}
      onUse={() => {
        showRestoreDialog = false;
        // Handle restore
      }}
      onDiscard={() => {
        pendingRestoreImage = null;
        showRestoreDialog = false;
      }}
    />
  {/if}

  {#if showModeSwitchDialog}
    <ModeSwitchDialog
      onConfirm={confirmModeSwitch}
      onCancel={cancelModeSwitch}
    />
  {/if}
</div>

<style>
  .app {
    height: 100%;
  }

  .redirect-screen {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: 2rem;
    background: #f8f9fa;
  }

  .redirect-content {
    text-align: center;
    max-width: 320px;
  }

  .redirect-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: #22c55e;
    color: white;
    font-size: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1.5rem;
  }

  .redirect-content h2 {
    font-size: 1.25rem;
    font-weight: 600;
    margin: 0 0 0.75rem;
    color: #1a1a1a;
  }

  .redirect-content p {
    font-size: 1rem;
    color: #666;
    margin: 0 0 0.5rem;
    line-height: 1.5;
  }

  .redirect-hint {
    font-size: 0.875rem;
    color: #999;
    margin-top: 1rem;
  }
</style>
